/**
 * Planning a recursive delete, and nothing that touches a DOM.
 *
 * Separated from the panel for the reason the upload state machine is: the
 * decisions here — what refuses a delete outright, and in what order the nodes
 * come off — are the ones worth testing without a browser, and the ones a
 * future reader will want to check without reading JSX.
 *
 * # There is still no `rm -r` anywhere
 *
 * `internal/hostfs/ops.go` removes exactly one entry per call: `rmdir` on a
 * directory, so the kernel refuses a non-empty one, and an unlink on everything
 * else. This module plans the loop that a server-side `recursive: true` would
 * have hidden. ADR-0027 §2 records that honestly — writing the loop in
 * JavaScript changes who wrote it, not what an operator can do from a browser —
 * and records what genuinely survives: every node is one confined, audited act,
 * and one exit code can never stand for four thousand deletions.
 *
 * # Every refusal is decided up front, for the whole tree
 *
 * Each rule below answers "should this happen at all", and answers it from the
 * walk plus the row that was clicked, before the confirmation opens. Nothing
 * here refuses halfway. A delete that stops at node 41 leaves a tree whose
 * remaining shape nobody chose and whose explanation is an error about
 * whichever directory happened to be next.
 *
 * The row is passed in ([RemovalBase]) rather than inferred, because the walk
 * runs with `-mindepth 1` and so describes every node except the one every plan
 * contains: the base. That gap was real — a base named `.env` was planned,
 * emptied, and only then refused by the server's deny-list.
 *
 * # What the walk does not show, and therefore neither does this
 *
 * `/tree` filters partial uploads out. That is right for a mirror and a trap
 * here: a directory holding nothing but somebody's interrupted upload comes back
 * *empty*, this plans no removals inside it, and its `rmdir` returns exit 10.
 * The plan is not wrong and cannot be made right from the walk — the `ErrNotEmpty`
 * mapping is what turns that into a sentence, which is why ADR-0027 calls it
 * load-bearing rather than tidy.
 */

import type { HostFileTree, HostTreeNode } from './api'
import { relPath } from './transfers'

/**
 * How many nodes one delete may carry.
 *
 * Deliberately not `hostfs.MaxWalkEntries` (50,000), which bounds what a *walk*
 * may answer. This bounds what a *browser click* may destroy, and those are not
 * the same question: above it the console refuses and names the count, so a
 * twenty-thousand-file tree becomes somebody's explicit decision at a shell.
 * It also keeps the audit volume sane, since every node writes its own row.
 *
 * A judgement rather than a measurement (ADR-0027, "Where this is uncertain").
 * If it proves wrong the number moves; the refusal above it must not.
 */
export const MAX_REMOVE_NODES = 500

/** Why a delete will not be attempted at all. Each carries what a message needs
 *  to name the cause — a count, or the file — because "cannot delete this" is a
 *  refusal an operator cannot act on. */
export type RemovalRefusal =
  | { refusal: 'truncated' }
  | { refusal: 'too-many'; count: number }
  | { refusal: 'denied'; item: string }
  /** The base resolved to the root itself. A caller's mistake rather than an
   *  operator's, and refused here because of what the mistake costs: without
   *  this the plan is every child of the registered root, removed, followed by
   *  a 400 on the one path the server rejects as empty. The whole point of
   *  deciding refusals up front is that this one arrives before the tree is
   *  gone rather than after. */
  | { refusal: 'root' }

/** What the delete will do, in the order it will do it. The counts are measured
 *  from the walk rather than composed afterwards: a confirmation that cannot say
 *  what is being destroyed is a formality. */
export interface RemovalPlan {
  /** Full paths under the root, ready for the API, deepest first and the base
   *  last. `order.length === files + dirs`. */
  order: string[]
  files: number
  /** Counts the base when the base is a directory, because it is one of the
   *  removals. A confirmation that says "two folders" and performs three
   *  `rmdir`s understates what it is about to destroy, which is the one
   *  direction a confirmation must never be wrong in — and one that says
   *  "1 folder" over a file is describing something that is not there. */
  dirs: number
}

/**
 * What the base is, which the walk cannot say.
 *
 * `hostfs.Walk` runs with `-mindepth 1`, so the base is not among
 * `tree.entries` — and the base is the one path present in every plan. It was
 * therefore also the one path that went through none of the rules below, which
 * is a hole with a shape: a directory whose *own* name is on the deny list —
 * `.env` is the common one, a virtualenv — had everything inside it planned and
 * removed, and then the server refused the final `rmdir` with the 403 that
 * should have refused the whole thing before anything was destroyed. Exactly
 * the half-finished delete the deny rule below exists to prevent, entered
 * through the node the walk does not describe.
 *
 * Required rather than defaulted. A caller that need not pass it is a caller
 * that can forget it, and a check nobody can fail is one this codebase has
 * shipped before.
 */
export interface RemovalBase {
  /** Whether the base is a directory: a file is one unlink and no `rmdir`, and
   *  the confirmation's counts have to say which. A symlink is not a directory
   *  here, whatever it points at — see [isDir]. */
  dir: boolean
  /** `HostFileEntry.denied` from the listing that drew the row, which is the
   *  same `hostfiles.DeniedName` verdict the server will reach on the delete. */
  denied?: boolean
}

/** How deep a path is, in "/"-separated segments. A child is always strictly
 *  deeper than its parent, which is the whole of the ordering rule. */
function depthOf(path: string): number {
  return path.split('/').length
}

/**
 * A directory, for the purpose of choosing between an unlink and an `rmdir`.
 *
 * A symlink is never one, whatever it points at. `removeScript` tests `-L`
 * first and unlinks the name without following it — following one would destroy
 * something outside the root — so a link goes in with the files even where the
 * walk also called it a directory.
 */
function isDir(n: HostTreeNode): boolean {
  return n.dir && !n.symlink
}

/**
 * The order a subtree comes off in, or the reason it will not.
 *
 * `base` is relative to the root, as the API takes it, and `tree.entries` are
 * relative to `base` — the walk uses `-mindepth 1`, so the base itself is not
 * among them and is appended here. `node` is what the listing knows about that
 * base, which is the half of the plan the walk cannot supply.
 */
export function planRemoval(
  tree: HostFileTree,
  base: string,
  node: RemovalBase,
): RemovalPlan | RemovalRefusal {
  // Truncation is checked first because it invalidates the checks below it: a
  // prefix of a tree has a meaningless count and an incomplete deny set. It is
  // also the sharpest refusal available here — deleting an arbitrary prefix of a
  // directory is the worst outcome this screen can produce, which is the same
  // non-negotiable rule the mirror states (ADR-0026 §11, ADR-0027 §2).
  if (tree.truncated) return { refusal: 'truncated' }

  if (tree.entries.length > MAX_REMOVE_NODES) {
    return { refusal: 'too-many', count: tree.entries.length }
  }

  // Trimmed once, because the base is the one path here that is not composed by
  // `relPath` and would otherwise be the only member of `order` still carrying a
  // caller's stray slash — the same string spelled two ways, in the array a
  // destructive loop walks. Trimmed *before* the checks below, so that "/" and
  // "" cannot be two different answers to the same question.
  const dir = base.replace(/^\/+|\/+$/g, '')

  if (dir === '') return { refusal: 'root' }

  // The base first, because it is the node the walk never reports and therefore
  // the one a reader will assume is covered by the loop below. It is also the
  // node removed *last*, so a refusal it does not get here is a refusal that
  // arrives with the tree already gone.
  if (node.denied) return { refusal: 'denied', item: dir }

  // A denied name refuses the whole plan, and this is where a delete parts
  // company with the mirror. The mirror *skips* a denied node, counts it and
  // says so: the operator gets a folder two files short and a sentence naming
  // how many. Skipping here would leave that file on the host, its parent
  // therefore non-empty, and the `rmdir` failing with exit 10 partway up the
  // tree — a half-finished delete explained by an error about a directory
  // rather than about the credential-shaped file still sitting in it. Refusing
  // before anything is destroyed is the only version of this the operator can
  // act on. See ADR-0027 §3 for why the deny-list is honoured on a delete at
  // all, given that it is a guardrail and not a security boundary.
  const denied = tree.entries.find((n) => n.denied)
  if (denied) return { refusal: 'denied', item: relPath(dir, denied.rel) }

  const files: string[] = []
  const dirs: string[] = []
  for (const n of tree.entries) {
    ;(isDir(n) ? dirs : files).push(relPath(dir, n.rel))
  }

  // Sorted by depth explicitly, and deliberately not by reversing the walk.
  // `find` emits depth-first, so reversing its output happens to put every child
  // before its parent today — and taking that would make the correctness of a
  // destructive operation depend on the argument order of a shell script in
  // another package, with nothing at either end saying so. Depth is the property
  // itself rather than a proxy for it: a parent is a strict prefix of its child
  // and therefore strictly shallower, so this cannot put a directory before
  // something inside it however the walk was emitted.
  dirs.sort((a, b) => depthOf(b) - depthOf(a))

  return {
    // Files at any depth may go first, because every `rmdir` comes after all of
    // them; only the directories need ordering among themselves.
    order: [...files, ...dirs, dir],
    // The base counts as whichever it is. `removeScript` unlinks a file and
    // `rmdir`s a directory, so a plan whose base is a file performs no `rmdir`
    // at all — and a confirmation is the one sentence that must not describe
    // something other than what is about to happen.
    files: files.length + (node.dir ? 0 : 1),
    dirs: dirs.length + (node.dir ? 1 : 0),
  }
}
